import {
  ActionError,
  Button,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  plural,
  toast,
} from '@westhetics/ui'
import { useRef, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useCreateNetwork } from './api'
import { NetworkClinicsBlock, NetworkIdentificationBlock, NetworkSharingBlock } from './blocks'
import {
  EMPTY_NETWORK_FORM,
  NETWORK_FORM_KEYS,
  networkFieldKey,
  networkInput,
  validateNetworkForm,
  type NetworkFormState,
} from './form'

/**
 * Nova rede (docs/areas/administracao.md, "Nova rede"; RN-ADM-54, RT-06). Arquétipo: cadastro, só
 * de root. Um nome e uma decisão: se as clínicas leem os pacientes umas das outras. As clínicas
 * entram aqui ou pelo cadastro de cada uma.
 */
export function NewNetworkPage() {
  const navigate = useNavigate()
  const create = useCreateNetwork()
  const form = useFormState<NetworkFormState>(() => EMPTY_NETWORK_FORM, validateNetworkForm)
  const formRef = useRef<HTMLFormElement>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(NETWORK_FORM_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    create.mutate(networkInput(form.values), {
      onSuccess: (network) => {
        const count = network.clinics.length
        toast(
          network.sharesPatients && count > 0
            ? `Rede ${network.name} criada — ${plural(count, 'clínica')} passam a ler pacientes umas das outras`
            : `Rede ${network.name} criada`,
        )
        navigate(routes.admin.network(network.id))
      },
      onError: (error) => {
        form.refuse(placeFieldErrors(error, networkFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  const failure = create.isError ? placeFieldErrors(create.error, networkFieldKey) : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  const existing =
    create.error && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.existing as { id: string } | undefined)
      : undefined

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Redes', href: routes.admin.networks }, { label: 'nova' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Nova rede"
            subtitle="Um nome e uma decisão: se as clínicas leem os pacientes umas das outras. As clínicas entram aqui ou pelo cadastro de cada uma."
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title="Não foi possível criar a rede"
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : `${errorMessage(create.error)} Nada foi criado; o que você digitou continua na tela.`
                }
                actions={
                  existing ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.admin.network(existing.id)}>Abrir a rede existente</Link>
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            <NetworkIdentificationBlock form={form} />
            <NetworkSharingBlock form={form} />
            <NetworkClinicsBlock form={form} error={form.errors.clinics} />

            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.networks}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={create.isPending} loadingLabel="Criando a rede…">
                  Criar rede
                </Button>
              }
            />
          </div>
        </ReadingColumn>
      </Page>
    </form>
  )
}
